/**
 * Role-based renderer wiadomosci WiseAi.
 *
 * Zamiast renderowac surowy markdown blob z `assistant.content`, iterujemy po
 * strukturalnych wiadomosciach z backendu i wybieramy dedykowany komponent:
 *  - `user` → bubble usera,
 *  - `assistant` → bubble z markdown (lead text, bez duplikowania kart produktow),
 *  - `tool_call_request` → ToolCallCard ze stanem pending lub done + dispatcher widoku,
 *  - `tool` → pominiete (wchlonal je rodzic tool_call_request),
 *  - `log` → pominiete.
 */

import React from 'react';
import ReactMarkdown from 'react-markdown';

import { WiseAiStructuredMessage } from 'api';
import styles from 'theme/components/containers/ChatBase/WiseAi/WiseAiMessages.module.scss';

import { ToolCallCard } from './ToolCallCard';
import { ProductsResultView } from './ProductsResultView';
import {
  ClientsListView,
  OffersListView,
  TasksListView,
  DashboardSummaryView
} from './TraderListsView';
import { resolveToolMeta, safeParseToolResult } from './toolMeta';

interface WiseAiMessagesProps {
  messages: WiseAiStructuredMessage[];
  includeDetails: boolean;
}

/**
 * Czysci `assistant.content` z duplikatu kart produktow.
 * Asystent w odpowiedzi zwraca lead text + `<div class="product-card">` z karta dla
 * kazdego produktu. Karty pochodza juz z toola — wycinamy je, zeby nie duplikowac.
 */
const extractLeadText = (content: string): string => {
  if (!content) return '';
  // Wytnij wszystkie bloki div z kartami produktow (asystent generuje je sam),
  // tail typu "Znalazłem łącznie 373 produkty..." (powtarza summary kart),
  // pusta lista numerowana "1. \n2. \n" (zostaja po wyciuciu divow),
  // a takze marker [truncated] dorzucany przez RemoteApiToolDispatcher.
  const cleaned = content
    .replace(/<div\b[\s\S]*?<\/div>/gi, '')
    .replace(/^\s*\d+\.\s*$/gm, '')
    .replace(/Znalazłem.*?(?:łącznie|w sumie)\s*\d+\s*produkt[a-zż]*[.!]?/gi, '')
    .replace(/\s*[.…]+\s*\[truncated\]\s*$/i, '')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
  return cleaned;
};

const extractTotalCount = (parsed: unknown): number | undefined => {
  if (!parsed || typeof parsed !== 'object') return undefined;
  const p = parsed as Record<string, unknown>;
  const response = (p.response ?? p) as Record<string, unknown> | undefined;
  if (Array.isArray(response)) return response.length;
  if (!response || typeof response !== 'object') return undefined;
  const total = response.total_count ?? response.totalCount;
  if (typeof total === 'number') return total;
  const items = response.items ?? response.data ?? response.records;
  if (Array.isArray(items)) return items.length;
  return undefined;
};

const summarizeToolResult = (
  functionName: string,
  parsed: unknown
): string | undefined => {
  const meta = resolveToolMeta(functionName);
  const total = extractTotalCount(parsed);
  if (total === undefined) return undefined;
  const NOUNS: Record<string, [string, string]> = {
    products: ['produkt', 'produktów'],
    clients: ['klient', 'klientów'],
    offers: ['oferta', 'ofert'],
    tasks: ['zadanie', 'zadań'],
    carts: ['koszyk', 'koszyków'],
    deals: ['transakcja', 'transakcji']
  };
  const noun = NOUNS[meta.viewType];
  if (!noun) return `${total}`;
  return `${total} ${total === 1 ? noun[0] : noun[1]}`;
};

const renderToolView = (functionName: string, parsed: unknown) => {
  const meta = resolveToolMeta(functionName);
  switch (meta.viewType) {
    case 'products':
      return <ProductsResultView payload={parsed as Parameters<typeof ProductsResultView>[0]['payload']} />;
    case 'clients':
      return <ClientsListView payload={parsed} />;
    case 'offers':
      return <OffersListView payload={parsed} />;
    case 'tasks':
      return <TasksListView payload={parsed} />;
    case 'dashboard':
      return <DashboardSummaryView payload={parsed} />;
    default:
      return null;
  }
};

export const WiseAiMessages: React.FC<WiseAiMessagesProps> = ({ messages, includeDetails }) => {
  // Mapa tool_call_id → wiadomosc tool result, dla szybkiego lookup w O(1).
  // Backend uzywa role=`tool` zgodnie z konwencja OpenAI; akceptujemy tez
  // alias `tool_call_result` jezeli sie pojawi.
  const toolResultByCallId = new Map<string, WiseAiStructuredMessage>();
  messages.forEach((m) => {
    if ((m.role === 'tool' || m.role === 'tool_call_result') && m.toolCallId) {
      toolResultByCallId.set(m.toolCallId, m);
    }
  });


  return (
    <>
      {messages.map((m) => {
        const role = m.role;

        if (role === 'user') {
          return (
            <div key={m.id} className={`message-user ${styles.row}`}>
              <div className={`message-bubble ${styles.userBubble}`}>
                <p>{m.content}</p>
              </div>
            </div>
          );
        }

        if (role === 'assistant') {
          const lead = extractLeadText(m.content);
          if (!lead) return null;
          return (
            <div key={m.id} className={`message-assistant ${styles.row}`}>
              <div className={`message-bubble ${styles.assistantBubble}`}>
                <ReactMarkdown>{lead}</ReactMarkdown>
              </div>
            </div>
          );
        }

        if (role === 'tool_call_request') {
          const calls = m.toolCalls ?? [];
          return (
            <div key={m.id} className={`message-assistant ${styles.row}`}>
              <div className={styles.toolGroup}>
                {calls.map((call) => {
                  const result = toolResultByCallId.get(call.id);
                  const isDone = !!result;
                  const parsed = result ? safeParseToolResult(result.content) : null;
                  const summary = isDone
                    ? summarizeToolResult(call.function.name, parsed)
                    : undefined;
                  const view = isDone ? renderToolView(call.function.name, parsed) : null;
                  return (
                    <div key={call.id} className={styles.toolItem}>
                      <ToolCallCard
                        functionName={call.function.name}
                        status={isDone ? 'done' : 'pending'}
                        resultSummary={summary}
                        argumentsJson={includeDetails ? call.function.arguments : undefined}
                        rawResult={includeDetails && result ? result.content : undefined}
                      />
                      {view && <div className={styles.toolView}>{view}</div>}
                    </div>
                  );
                })}
              </div>
            </div>
          );
        }

        if (role === 'log') {
          // Backend zwraca `role=log` dla zdarzen orchestratora — m.in. komunikatow
          // bledow ("All models failed...", "API key not configured"). Pokazujemy
          // tylko te, ktore wygladaja na bledy uzytkownika; reszte (np. statystyki)
          // ignorujemy.
          const isError = /fail|error|cannot|invalid|not configured|missing/i.test(m.content);
          if (!isError) return null;
          return (
            <div key={m.id} className={`message-assistant ${styles.row}`}>
              <div className={styles.errorBubble}>
                <strong>Błąd:</strong> {m.content}
              </div>
            </div>
          );
        }

        // role: tool, inne — pomijamy (tool jest wchlaniany przez tool_call_request)
        return null;
      })}
    </>
  );
};
